/**
 * RecoveryLogin — Ultra Yönetici kurtarma kodu ile şifre sıfırlama.
 *
 * Route: `/recovery` (public, no auth required).
 * 
 * Kullanıcı: e-posta + kurtarma kodu (12 karakter XXXX-XXXX-XXXX
 * formatında) + yeni şifre + yeni şifre onayı girer. Backend kod
 * hash'ini doğrular, şifreyi sıfırlar, tüm oturumları iptal eder,
 * kodu tek kullanımlık olarak invalidate eder ve doğrudan taze
 * token ile /app/dashboard'a yönlendirir.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/ui/password-input";
import { PasswordStrength, isPasswordValid } from "../components/auth/PasswordStrength";
import { SerpixWordmark } from "../components/brand/SerpixWordmark";

export default function RecoveryLogin() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const login = useAuthStore((s) => s.login);

  const pwValid = isPasswordValid(password);
  const pwMatch = confirm.length > 0 && password === confirm;
  const showMismatch = confirm.length > 0 && !pwMatch;
  const canSubmit =
    email && code && pwValid && pwMatch && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/auth/recovery-code/use", {
        email,
        code,
        new_password: password,
      });
      // Password is now reset on the server. Fresh login using the
      // new credentials so authStore hydrates cleanly (WS, i18n, etc).
      await login(email, password);
      toast.success("Şifren sıfırlandı, girişin başarılı");
      nav("/app/dashboard");
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Kurtarma başarısız oldu",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-violet-950 px-6 py-12">
      <div
        data-testid="recovery-page"
        className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900/80 p-8 shadow-2xl backdrop-blur"
      >
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-white">
          <SerpixWordmark size="sm" />
        </Link>
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-violet-500/30 bg-violet-500/10 p-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-violet-300" />
          <div className="text-[11px] leading-relaxed text-violet-100">
            Bu sayfa yalnızca <b>Ultra Yönetici</b> hesabı içindir. Standart
            şifre sıfırlama için{" "}
            <Link to="/forgot-password" className="underline">
              Şifremi unuttum
            </Link>{" "}
            sayfasını kullanın.
          </div>
        </div>
        <h1 className="mb-1 flex items-center gap-2 font-display text-2xl font-semibold text-white">
          <KeyRound className="h-5 w-5 text-violet-300" />
          Kurtarma Kodu
        </h1>
        <p className="mb-6 text-xs text-zinc-400">
          Kayıt sırasında oluşturduğun 12 haneli kurtarma kodunu kullanarak
          şifreni sıfırla ve hesabına yeniden giriş yap.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">
              E-posta
            </Label>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ultra@sirket.com"
              required
              data-testid="recovery-email"
              className="mt-1 border-zinc-700 bg-zinc-800 text-white placeholder:text-zinc-500"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">
              Kurtarma Kodu
            </Label>
            <Input
              type="text"
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX-XXXX"
              required
              data-testid="recovery-code"
              className="mt-1 border-zinc-700 bg-zinc-800 font-mono tracking-widest text-white placeholder:text-zinc-500"
              maxLength={20}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">
              Yeni Şifre
            </Label>
            <PasswordInput
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="recovery-new-password"
              testIdToggle="recovery-new-password-toggle"
              className="mt-1 border-zinc-700 bg-zinc-800 text-white placeholder:text-zinc-500"
            />
            <PasswordStrength password={password} hideWhenEmpty />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">
              Yeni Şifre (Tekrar)
            </Label>
            <PasswordInput
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              aria-invalid={showMismatch ? "true" : "false"}
              data-testid="recovery-new-password-confirm"
              testIdToggle="recovery-new-password-confirm-toggle"
              className="mt-1 border-zinc-700 bg-zinc-800 text-white placeholder:text-zinc-500"
            />
            {showMismatch && (
              <p
                className="mt-1 text-xs font-medium text-rose-400"
                data-testid="recovery-password-mismatch"
              >
                Şifreler eşleşmiyor
              </p>
            )}
          </div>
          {error && (
            <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={!canSubmit}
            data-testid="recovery-submit"
            className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-500/30 hover:from-violet-700 hover:to-fuchsia-700 disabled:opacity-50"
          >
            {loading ? "Doğrulanıyor..." : "Şifreyi sıfırla ve giriş yap"}
          </Button>
        </form>
        <Link
          to="/login"
          className="mt-6 block text-xs text-zinc-500 hover:text-zinc-200"
        >
          ← Girişe dön
        </Link>
      </div>
    </div>
  );
}
